import React from 'react';
import * as S from './styles';
import { IconButtonProps } from './types';

function ForwardedIconButton<
  T extends Element = HTMLButtonElement,
  As extends React.ElementType = 'button',
>(
  {
    as,
    children,
    color = 'primary',
    variant = 'default',
    size = 'small',
    ...props
  }: IconButtonProps<As>,
  ref: React.ForwardedRef<T>,
): React.ReactElement | null {
  const Component: React.ElementType = as ?? 'button';

  return (
    <S.Root
      {...props}
      forwardedAs={Component}
      $color={color}
      $variant={variant}
      $size={size}
      ref={ref}
    >
      {children}
    </S.Root>
  );
}

/**
 * Component for a clickable icon.
 *
 * If the button does not have a tooltip, `aria-label` or `aria-labelledby` should
 * be used to describe the button's purpose.  If a tooltip is used, `aria-labelledby`
 * will be added automatically referencing the tooltip as the description of the
 * button.
 */
export const IconButton = React.forwardRef(ForwardedIconButton) as <
  T extends Element = HTMLButtonElement,
  As extends React.ElementType = 'button',
>(
  props: IconButtonProps<As> & { ref?: React.Ref<T> },
) => React.ReactElement | null;

export default IconButton;
